import { useCallback, useEffect, useMemo, useState } from "react";
import { Percent, Search, UserRoundCog } from "lucide-react";
import { api } from "../../services/api";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackCustomers = [
  { _id: "preview-1", name: "Miller Sewing Co.", email: "orders@millersewing.example", phone: "", customerDiscount: { percent: 10, note: "Repeat buyer" }, isActive: true },
];

export default function Customers() {
  const [search, setSearch] = useState("");
  const [discounts, setDiscounts] = useState({});
  const { dismiss, toast, toasts } = useToast();

  const loadCustomers = useCallback(async () => {
    const data = await api.get(`/customers?search=${encodeURIComponent(search)}`);
    return data.customers.length ? data.customers : fallbackCustomers;
  }, [search]);

  const { data: customers, error, loading, reload, setData } = useAdminResource(loadCustomers, fallbackCustomers);

  useEffect(() => {
    if (error && !loading) toast(`Using preview customers: ${error}`, "warning");
  }, [error, loading, toast]);

  const visibleCustomers = useMemo(() => customers.filter((customer) =>
    [customer.name, customer.email, customer.phone].join(" ").toLowerCase().includes(search.toLowerCase())
  ), [customers, search]);
  const pagination = usePagination(visibleCustomers, [search], 10);

  const updateDiscount = async (customer) => {
    const draft = discounts[customer._id] || {};
    const percent = draft.percent ?? customer.customerDiscount?.percent ?? 0;
    const note = draft.note ?? customer.customerDiscount?.note ?? "";
    if (String(customer._id).startsWith("preview")) return;

    try {
      const data = await api.patch(`/customers/${customer._id}/discount`, { percent: Number(percent), note });
      setData((current) => current.map((item) => item._id === data.customer._id ? data.customer : item));
      toast(`Discount saved for ${data.customer.name}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const customersWithDiscount = customers.filter((customer) => Number(customer.customerDiscount?.percent || 0) > 0).length;

  return (
    <div className="space-y-6 p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Customer Accounts</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Customer discounts</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Set a different discount percent for each customer. Invoices and phone orders can apply this automatically.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", onClick: reload }]} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <InsightCard title="Customers" value={customers.length} note="User accounts available for discounts" />
        <InsightCard title="Discounted" value={customersWithDiscount} note="Customers with custom pricing" tone="money" />
        <InsightCard title="Default Discount" value="0%" note="Applied when no custom discount exists" tone="info" />
      </div>

      <section className="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3"><UserRoundCog className="text-[#4391d1]" /><h2 className="text-xl font-black text-gray-900">Customers</h2></div>
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={16} />
            <input className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pl-10 text-sm font-bold" onChange={(event) => setSearch(event.target.value)} placeholder="Filter customers" value={search} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
            <thead><tr className="text-xs font-black uppercase text-gray-400">{["Customer", "Email", "Current", "Discount %", "Note", "Status", "Action"].map((column) => <th className="px-6 py-4" key={column}>{column}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">
              {pagination.paginatedItems.map((customer) => {
                const draft = discounts[customer._id] || {};
                return (
                  <tr key={customer._id}>
                    <td className="px-6 py-4 font-black text-gray-900">{customer.name}</td>
                    <td className="px-6 py-4 font-semibold text-gray-600">{customer.email}</td>
                    <td className="px-6 py-4"><StatusPill tone={Number(customer.customerDiscount?.percent || 0) > 0 ? "info" : "neutral"}>{customer.customerDiscount?.percent || 0}%</StatusPill></td>
                    <td className="px-6 py-4"><input className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold" min="0" max="100" onChange={(event) => setDiscounts((current) => ({ ...current, [customer._id]: { ...draft, percent: event.target.value } }))} type="number" value={draft.percent ?? customer.customerDiscount?.percent ?? 0} /></td>
                    <td className="px-6 py-4"><input className="min-w-56 rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold" onChange={(event) => setDiscounts((current) => ({ ...current, [customer._id]: { ...draft, note: event.target.value } }))} value={draft.note ?? customer.customerDiscount?.note ?? ""} /></td>
                    <td className="px-6 py-4"><StatusPill tone={customer.isActive ? "good" : "bad"}>{customer.isActive ? "active" : "inactive"}</StatusPill></td>
                    <td className="px-6 py-4"><button className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-4 py-2 text-xs font-black text-white hover:bg-blue-600" onClick={() => updateDiscount(customer)} type="button"><Percent size={14} /> Save</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationControls pagination={pagination} />
      </section>
    </div>
  );
}
